import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WebsiteResponse } from "@joogoos/contracts";
import { api, here } from "../api.js";
import { Icon } from "../ui/icons.js";

/**
 * Your website — the one Joogoos makes for a business that has none
 * ([28](../../../../docs/28-the-website-joogoos-makes.md), ADR 0047).
 *
 * Setting up is three things on Cloudflare's side and one on ours, in the order they have to happen: open an
 * account, buy the domain, make the token from our link and paste it. After that the screen is the page itself —
 * designed whole by the model and made safe by code (ADR 0047 §4 as amended) — a box to say what to change, and
 * Publish. Nothing reaches the customer's site that they have not seen here.
 */
export function WebsitePanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["website"], queryFn: () => api.website(),
    // While it is being designed or sent, ask again every few seconds: the worker finishes on its own and the screen
    // should notice.
    refetchInterval: (query) => (query.state.data?.status === "publishing" || query.state.data?.writing ? 4_000 : false),
  });
  const keep = (d: WebsiteResponse) => {
    qc.setQueryData(["website"], d);
    void qc.invalidateQueries({ queryKey: ["site"] });
  };
  if (q.isPending) return null;
  if (!q.data) return <p className="text-bad text-sm">{q.error?.message ?? "The website could not be read."}</p>;
  const w = q.data;

  if (w.otherWebsite) {
    return (
      <Box title="You already have a website">
        <p className="text-[13px] leading-relaxed">Your business page says your website is <b>{w.otherWebsite}</b>, so there is nothing to build: Analytics counts it and SEO publishes on it. Joogoos only makes a website for a business that has none, so the two never compete.</p>
      </Box>
    );
  }
  if (!w.available) {
    return <Box title="Not available yet"><p className="text-[13px]">This server cannot keep a Cloudflare token safely, so it cannot publish a website.</p></Box>;
  }

  return (
    <div className="grid gap-4 items-start lg:grid-cols-[1fr_1.25fr]">
      <div className="flex flex-col gap-4 min-w-0">
        <Account w={w} keep={keep} />
        {w.cloudflare ? <Domain w={w} keep={keep} /> : null}
        <Contact w={w} keep={keep} />
      </div>
      <div className="flex flex-col gap-4 min-w-0">
        <Page w={w} keep={keep} />
      </div>
    </div>
  );
}

type Keep = (d: WebsiteResponse) => void;

function Box({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="bg-panel border border-line rounded-[10px] px-4 py-3.5 flex flex-col gap-2.5" aria-label={title}>
      <div className="flex flex-wrap items-baseline gap-2"><h2 className="text-[14px] font-bold">{title}</h2>{aside ? <span className="ml-auto text-[11.5px] text-muted">{aside}</span> : null}</div>
      {children}
    </section>
  );
}

function Step({ n, done, title, children }: { n: number; done: boolean; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className={`w-6 h-6 shrink-0 rounded-full grid place-items-center text-[12px] font-bold ${done ? "bg-good text-white" : "bg-ink text-white"}`}>
        {done ? <Icon name="check" size={12} /> : n}
      </span>
      <div className="flex flex-col gap-1.5 min-w-0 flex-1">
        <span className="text-[13px] font-semibold">{title}</span>
        {children}
      </div>
    </li>
  );
}

const linkButton = "self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-semibold";

/** The three steps on Cloudflare's side, then the paste. Once connected, one line and a way to disconnect. */
function Account({ w, keep }: { w: WebsiteResponse; keep: Keep }) {
  const [token, setToken] = useState("");
  const connect = useMutation({ mutationFn: () => api.websiteToken(token), onSuccess: (d) => { setToken(""); keep(d); } });
  const forget = useMutation({ mutationFn: () => api.websiteForget(), onSuccess: keep });

  if (w.cloudflare) {
    return (
      <Box title="Your Cloudflare account" aside="connected">
        <p className="text-[13px]">Joogoos publishes to <b>{w.cloudflare.accountName}</b> with the token ending <span className="font-mono">…{w.cloudflare.tokenHint}</span>. The site, the domain and the account are yours; delete the token in Cloudflare and Joogoos can change nothing.</p>
        <button onClick={() => forget.mutate()} disabled={forget.isPending} className="self-start text-[12px] text-muted underline underline-offset-2">Disconnect</button>
        {forget.error ? <p className="text-bad text-xs">{forget.error.message}</p> : null}
      </Box>
    );
  }
  return (
    <Box title="Where your website will live" aside="about five minutes">
      <p className="text-[12.5px] leading-relaxed text-muted">Your website lives in your own Cloudflare account, in your name. Hosting a site like this is free there. Joogoos writes it and sends it; it never owns it.</p>
      <ol className="flex flex-col gap-4 mt-1">
        <Step n={1} done={false} title="Open a free Cloudflare account">
          <p className="text-[12.5px] text-muted">Free, and no card is asked for. Verify your email, then come back here.</p>
          <a href={w.links.signUp} target="_blank" rel="noopener noreferrer" className={linkButton}>Open Cloudflare <Icon name="globe" size={12} /></a>
        </Step>
        <Step n={2} done={false} title="Buy your domain">
          <p className="text-[12.5px] text-muted">
            The address people type, like <i>yourbakery.co.uk</i>. Cloudflare sells domains at cost, usually $10–$15 a year, paid to them and in your name.
            You can skip this and start on a free address, but Google takes a site on its own domain far more seriously.
          </p>
          <a href={w.links.buyDomain} target="_blank" rel="noopener noreferrer" className={linkButton}>Buy a domain on Cloudflare <Icon name="globe" size={12} /></a>
        </Step>
        <Step n={3} done={false} title="Give Joogoos permission to publish">
          <p className="text-[12.5px] text-muted">
            This opens Cloudflare with the permission already chosen. Scroll down, press <b>Continue to summary</b>, then <b>Create Token</b>, and copy the token it shows you once.
          </p>
          <a href={w.links.token} target="_blank" rel="noopener noreferrer" className={linkButton}>Make the token <Icon name="lock" size={12} /></a>
          <form className="flex flex-wrap gap-2 mt-1" onSubmit={(e) => { e.preventDefault(); if (token.trim()) connect.mutate(); }}>
            <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste the token here" aria-label="Cloudflare token" autoComplete="off" spellCheck={false}
              className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-line-2 bg-page text-[13px] font-mono" />
            <button type="submit" disabled={!token.trim() || connect.isPending} className="px-3.5 py-2 rounded-lg bg-ink text-white text-[13px] font-semibold disabled:opacity-40">
              {connect.isPending ? "Checking…" : "Connect"}
            </button>
          </form>
          {connect.error ? <p className="text-bad text-xs">{connect.error.message}</p> : null}
          <p className="text-[11.5px] text-muted">Cloudflare lets this token publish to any site in the account, not only this one. If a website you care about already lives in that account, open a fresh account for this one.</p>
        </Step>
      </ol>
    </Box>
  );
}

/** Which of their domains it goes on, read from Cloudflare — so a domain bought a minute ago is already here. */
function Domain({ w, keep }: { w: WebsiteResponse; keep: Keep }) {
  const qc = useQueryClient();
  const choose = useMutation({ mutationFn: (zoneId: string | null) => api.websiteDomain(zoneId), onSuccess: keep });
  const zones = w.cloudflare?.zones ?? [];
  return (
    <Box title="Your address" aside={w.domain ? w.domain.name : "free address"}>
      {zones.length ? (
        <div className="flex flex-col gap-1.5">
          {zones.map((z) => (
            <label key={z.id} className="flex items-center gap-2 text-[13px]">
              <input type="radio" name="zone" checked={w.domain?.zoneId === z.id} onChange={() => choose.mutate(z.id)} disabled={choose.isPending} />
              <span className="font-semibold">{z.name}</span>
            </label>
          ))}
          <label className="flex items-center gap-2 text-[13px]">
            <input type="radio" name="zone" checked={!w.domain} onChange={() => choose.mutate(null)} disabled={choose.isPending} />
            <span>The free address Cloudflare gives, for now</span>
          </label>
        </div>
      ) : (
        <p className="text-[12.5px] leading-relaxed">
          No domain in this account yet, so the site will start on a free <i>workers.dev</i> address. It works and it is counted, but it is worth almost nothing to Google.{" "}
          <a href={w.links.buyDomain} target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2">Buy one on Cloudflare</a>, then press Look again.
        </p>
      )}
      {w.cloudflare?.zonesError ? <p className="text-bad text-xs">{w.cloudflare.zonesError}</p> : null}
      <button onClick={() => void qc.invalidateQueries({ queryKey: ["website"] })} className="self-start text-[12px] text-coral font-semibold">Look again</button>
      {choose.error ? <p className="text-bad text-xs">{choose.error.message}</p> : null}
      {w.domain && w.address && !w.address.includes(w.domain.name) ? <p className="text-[12px] text-muted">Publish again to move the site onto {w.domain.name}.</p> : null}
    </Box>
  );
}

/** The two ways in. Every call and email link on the page is pointed at exactly what is typed here. */
function Contact({ w, keep }: { w: WebsiteResponse; keep: Keep }) {
  const [phone, setPhone] = useState(w.contact.phone);
  const [email, setEmail] = useState(w.contact.email);
  useEffect(() => { setPhone(w.contact.phone); setEmail(w.contact.email); }, [w.contact.phone, w.contact.email]);
  const save = useMutation({ mutationFn: () => api.websiteContact(phone, email), onSuccess: keep });
  const changed = phone !== w.contact.phone || email !== w.contact.email;
  return (
    <Box title="How people reach you" aside="the links on the page">
      <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" aria-label="Phone" inputMode="tel" className="px-3 py-2 rounded-lg border border-line-2 bg-page text-[13px]" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" aria-label="Email" type="email" className="px-3 py-2 rounded-lg border border-line-2 bg-page text-[13px]" />
        {changed ? <button type="submit" disabled={save.isPending} className="self-start px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">Save</button> : null}
        {save.error ? <p className="text-bad text-xs">{save.error.message}</p> : null}
      </form>
      <p className="text-[11.5px] text-muted">Every call and email from the site is counted on Analytics. There is no form: a form is a spam problem and somebody's inbox. Changed after the page was designed? The links follow at once; redesign to change the words around them.</p>
    </Box>
  );
}

/**
 * The page in a frame, from `srcdoc`: the HTML is fetched like any other read and handed to the frame, so nothing
 * here needs the app to be framable. Sandboxed with no scripts; same-origin only so its stylesheet and pictures load.
 */
function Preview({ versionId, contact }: { versionId: string; contact: string }) {
  // The buttons on the page are the contact details, so a change to them draws the page again.
  const html = useQuery({ queryKey: ["website", "preview", versionId, contact], queryFn: () => api.websitePreview(versionId), staleTime: Infinity });
  if (!html.data) return <div className="w-full h-[520px] rounded-lg border border-line bg-page grid place-items-center text-faint text-sm">{html.error?.message ?? "Drawing your page…"}</div>;
  return <iframe srcDoc={html.data} title="Your website" sandbox="allow-same-origin" className="w-full h-[520px] rounded-lg border border-line bg-white" />;
}

const STATUS: Record<WebsiteResponse["status"], string> = {
  none: "", draft: "Not published yet", publishing: "Sending it to Cloudflare…", live: "Live", failed: "Not published",
};

/** The page, what to change about it, and the button that sends it. */
function Page({ w, keep }: { w: WebsiteResponse; keep: Keep }) {
  const [ask, setAsk] = useState("");
  const write = useMutation({ mutationFn: (words: string) => api.websiteWrite(words), onSuccess: (d) => { setAsk(""); keep(d); } });
  const failed = w.writeError && !w.writing ? <p role="alert" className="text-bad text-xs">{w.writeError}</p> : null;
  const publish = useMutation({ mutationFn: () => api.websitePublish(), onSuccess: keep });
  const use = useMutation({ mutationFn: (id: string) => api.websiteUse(id), onSuccess: keep });
  const busy = write.isPending || w.writing;

  if (!w.current) {
    return (
      <Box title="Your page" aside={`about $${w.price} to design`}>
        <p className="text-[13px] leading-relaxed">One page, designed for your business by Anthropic's strongest model: written from your business page, in your colours, with your logo and photos, and a way to get in touch. Every design is checked before you see it — no scripts, nothing from anywhere but your own pictures — and you read it here before anything goes out.</p>
        <button onClick={() => write.mutate("")} disabled={busy} className="self-start px-4 py-2 rounded-lg bg-coral text-white text-[13px] font-semibold disabled:opacity-40">
          {busy ? "Designing your page… a few minutes" : `Design my page · about $${w.price}`}
        </button>
        {busy ? <p className="text-[12px] text-muted">You can leave this screen; the page will be here when it is ready.</p> : null}
        {write.error ? <p className="text-bad text-xs">{write.error.message}</p> : null}
        {failed}
      </Box>
    );
  }

  const unpublished = w.current.id !== w.publishedVersionId;
  const freeAddress = !!w.address && w.address.endsWith(".workers.dev");
  return (
    <>
      <Box title="Your page" aside={<a href={here(`/v1/website/preview/?v=${w.current.id}`)} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">open in a new tab</a>}>
        <Preview versionId={w.current.id} contact={`${w.contact.phone}|${w.contact.email}`} />
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-[12.5px] flex-1 min-w-[180px]">
            <b className={w.status === "failed" ? "text-bad" : ""}>{STATUS[w.status]}</b>
            {w.address && w.status !== "publishing" ? <> · <a href={w.address} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">{w.address.replace(/^https:\/\//, "")}</a></> : null}
            {w.status === "live" && unpublished ? <span className="text-muted"> · this version is not on it yet</span> : null}
          </span>
          <button onClick={() => publish.mutate()} disabled={!w.cloudflare || w.status === "publishing" || publish.isPending || (w.status === "live" && !unpublished)}
            title={w.cloudflare ? undefined : "Connect your Cloudflare account first"}
            className="px-4 py-2 rounded-lg bg-coral text-white text-[13px] font-semibold disabled:opacity-40">
            {w.status === "publishing" ? "Publishing…" : w.publishedVersionId ? "Publish this version" : "Publish"}
          </button>
        </div>
        {!w.cloudflare ? <p className="text-[12px] text-muted">Connect your Cloudflare account on the left to publish it. Publishing is free.</p> : null}
        {w.lastError ? <p role="alert" className="text-bad text-xs">{w.lastError}</p> : null}
        {publish.error ? <p className="text-bad text-xs">{publish.error.message}</p> : null}
        {freeAddress ? <p className="text-[11.5px] text-muted">A free workers.dev address is real and counted, and worth almost nothing to Google. Put the site on your own domain when you have one.</p> : null}
      </Box>
      <Box title="Change something" aside={`about $${w.price} a redesign`}>
        <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); if (ask.trim()) write.mutate(ask); }}>
          <textarea value={ask} onChange={(e) => setAsk(e.target.value)} rows={3} maxLength={1000} aria-label="What to change"
            placeholder="Say it the way you would to a person: “mention we deliver on Saturdays”, “darker and moodier”, “bigger photos”, “make it feel like a café, not a shop”."
            className="px-3 py-2 rounded-lg border border-line-2 bg-page text-[13px] leading-relaxed" />
          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" disabled={!ask.trim() || busy} className="px-3.5 py-2 rounded-lg bg-ink text-white text-[13px] font-semibold disabled:opacity-40">
              {busy ? "Redesigning… a few minutes" : "Change it"}
            </button>
            <button type="button" onClick={() => write.mutate("")} disabled={busy} className="text-[12px] text-muted underline underline-offset-2">Design a fresh one from the business page</button>
          </div>
          {write.error ? <p className="text-bad text-xs">{write.error.message}</p> : null}
          {failed}
        </form>
        <p className="text-[11.5px] text-muted">Every change is a new version: the one on your site stays until you publish another. Photos, logo and colours come from Photos and Your look.</p>
      </Box>
      {w.history.length > 1 ? (
        <Box title="Earlier versions">
          <ul className="flex flex-col divide-y divide-line -my-1">
            {w.history.map((h) => (
              <li key={h.id} className="flex items-center gap-2 py-1.5 text-[12.5px]">
                <span className="flex-1 min-w-0 truncate">{h.ask || "Designed from the business page"}</span>
                <span className="text-muted text-[11.5px] tabular-nums">{new Date(h.createdAt).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</span>
                {h.id === w.current!.id ? <span className="text-[11.5px] text-muted">showing</span>
                  : <button onClick={() => use.mutate(h.id)} disabled={use.isPending} className="text-[12px] text-coral font-semibold">Use this one</button>}
                {h.id === w.publishedVersionId ? <span className="text-[10.5px] font-bold uppercase tracking-wide text-good">live</span> : null}
              </li>
            ))}
          </ul>
        </Box>
      ) : null}
    </>
  );
}
